import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { getCrossID, getMappedFilters } from 'utils/filters';
import { useWeb5Context } from 'lib/Web5Context';
import { getSelectedFilters } from './utils';
import { isEqual } from 'utils/objects';

const useAddModal = ({
    entity,
    defaultFilters = [],
    extraSchema,
    extraMultiSchema,
    onConfirmAdd,
    onDeleteMultiple,
    onResetVisibles,
    visibleFilters,
    selectorsSchema,
    labels = {},
    onDelete,
    forceSelectAllTypes,
}) => {
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isOpenAddModal, setIsOpenAddModal] = useState(false);
    const oldSelectedFilters = useRef({});
    const tempVisibleFilters = useRef([]);
    const innerVisibleFilters = useRef([]);
    const initialSelectedFilters = useRef({});
    const isFirstLoad = useRef(true);
    const prevIsOpen = useRef(false);
    const { t } = useWeb5Context();

    const handleDeleteFilter = useCallback(
        (filter) => {
            oldSelectedFilters.current = selectedFilters;
            onDelete && onDelete(filter);
        },
        [oldSelectedFilters, onDelete, selectedFilters],
    );

    const onToggleAddModal = useCallback(() => {
        setIsOpenAddModal(!isOpenAddModal);
    }, [isOpenAddModal]);

    const onChangeSelectedFilters = useCallback(
        (newSelected) => {
            let finalSelected = getSelectedFilters(newSelected, entity);
            tempVisibleFilters.current = finalSelected;
            setSelectedFilters({ ...newSelected });
        },
        [setSelectedFilters, entity],
    );

    const handleCancelAdd = useCallback(() => {
        onChangeSelectedFilters(initialSelectedFilters.current);
        tempVisibleFilters.current = [];
    }, [onChangeSelectedFilters]);

    useEffect(() => {
        // Every time we open the modal we define the initialSelectedFilters, to have the
        // object available is we cancel and need to reset to the same state it was when opening
        if (isOpenAddModal && prevIsOpen.current !== isOpenAddModal) {
            prevIsOpen.current = true;
            initialSelectedFilters.current = JSON.parse(JSON.stringify(selectedFilters));
            oldSelectedFilters.current = JSON.parse(JSON.stringify(selectedFilters));
        } else if (!isOpenAddModal) {
            prevIsOpen.current = false;
        }
    }, [selectedFilters, isOpenAddModal]);

    const getUnselectedFilters = useCallback(() => {
        const innerGetUnselectedFilters = (oldFilters, newFilters) => {
            let oldSelected = [];
            let selected = [];
            if (Object.keys(oldFilters).length > 0) {
                oldSelected = Object.keys(oldFilters).reduce((arr, key) => {
                    if (oldFilters[key]?.options?.length > 0) {
                        arr = [...arr, ...(oldFilters[key]?.options || [])];
                    }
                    return arr;
                }, []);
            }
            if (Object.keys(newFilters).length > 0) {
                selected = Object.keys(newFilters).reduce((arr, key) => {
                    if (newFilters[key]?.options?.length > 0) {
                        arr = [...arr, ...(newFilters[key]?.options || [])];
                    }
                    return arr;
                }, []);
            }

            const unselectedFilters = oldSelected.reduce((arr, current) => {
                if (selected.length === 0 || !selected.includes(current)) {
                    arr.push(current);
                }
                return arr;
            }, []);

            return unselectedFilters;
        };

        if (extraMultiSchema?.length) {
            return extraMultiSchema.reduce((obj, schema) => {
                const { id } = schema;
                const unselectedFilters = innerGetUnselectedFilters(
                    oldSelectedFilters.current?.[id]?.options || {},
                    selectedFilters?.[id]?.options || {},
                );
                if (unselectedFilters.length > 0) obj[id] = unselectedFilters;
                return obj;
            }, {});
        } else {
            return innerGetUnselectedFilters(oldSelectedFilters.current, selectedFilters);
        }
    }, [extraMultiSchema, selectedFilters]);

    const handleConfirmAdd = useCallback(
        (selected) => {
            let newAddedFields = [];
            const unselectedFilters = getUnselectedFilters();

            const innerHandleConfirmAdd = (selectedObj, id) => {
                return (
                    (selectedObj &&
                        Object.keys(selectedObj).reduce((arr, key) => {
                            let options = selectedObj[key].options || [];
                            if (id && id !== entity.entity)
                                options = options.map((option) => getCrossID(option, id));
                            arr = [...arr, ...options];
                            return arr;
                        }, [])) ||
                    []
                );
            };

            if (extraMultiSchema?.length) {
                if (Object.entries(unselectedFilters).length > 0)
                    onDeleteMultiple(unselectedFilters);
                newAddedFields = extraMultiSchema.reduce((arr, schema) => {
                    const { id } = schema;
                    return selected[id]
                        ? [...arr, ...innerHandleConfirmAdd(selected[id].options, id)]
                        : arr;
                }, []);
            } else {
                if (unselectedFilters?.length > 0) onDeleteMultiple(unselectedFilters);
                newAddedFields = innerHandleConfirmAdd(selected);
            }

            oldSelectedFilters.current = JSON.parse(JSON.stringify(selected));
            innerVisibleFilters.current = [...(tempVisibleFilters.current || [])];
            tempVisibleFilters.current = [];
            onConfirmAdd && onConfirmAdd(newAddedFields);
            setIsOpenAddModal(false);
        },
        [getUnselectedFilters, extraMultiSchema, onConfirmAdd, entity?.entity, onDeleteMultiple],
    );

    const getInitialSelected = useCallback(
        ({ schema, visibleFilters, multiSchema, isReset = false }) => {
            let newInitialSelected = {};

            const innerGetInitialSelected = (currentSchema, currentVisible = []) => {
                return currentSchema.reduce((obj, group) => {
                    let options = [];
                    if (group?.options) {
                        options = group.options.reduce((arr, option) => {
                            if (currentVisible.includes(option.id)) {
                                arr.push(option.id);
                            }
                            return arr;
                        }, []);
                    }

                    obj[group.id] = {
                        isSelected:
                            (options.length > 0 && options.length === group.options.length) ||
                            false,
                        options,
                    };
                    return obj;
                }, {});
            };

            if (multiSchema?.length) {
                if (!multiSchema?.length > 0) return {};
                const mappedVisibleFilters = getMappedFilters(visibleFilters, entity.entity);

                newInitialSelected = multiSchema.reduce((obj, currentSchema) => {
                    const { id, schema } = currentSchema;
                    const options = innerGetInitialSelected(schema, mappedVisibleFilters?.[id]);
                    const isSelected =
                        isReset && forceSelectAllTypes
                            ? true
                            : Object.values(options).some(
                                  (option) => option.isSelected || option.options.length > 0,
                              );

                    obj[id] = {
                        isExpanded: true,
                        isSelected,
                        // In the case of entities with cross filters we want to keep the filters of the main entity
                        // always visible in the filters selection modal so we override the isSelected value with overrideIsSelected
                        overrideIsSelected: id === entity.entity,
                        options,
                    };
                    return obj;
                }, {});
            } else {
                if (!schema?.length > 0) return {};
                newInitialSelected = innerGetInitialSelected(schema, visibleFilters);
            }

            onChangeSelectedFilters({ ...newInitialSelected });
        },
        [entity?.entity, onChangeSelectedFilters, forceSelectAllTypes],
    );

    const onResetSelected = useCallback(
        (initialFilters) => {
            onChangeSelectedFilters(initialFilters);
            getInitialSelected({
                schema: extraSchema,
                visibleFilters: defaultFilters,
                multiSchema: extraMultiSchema,
                isReset: true,
            });
            tempVisibleFilters.current = [];
            innerVisibleFilters.current = defaultFilters;

            onResetVisibles && onResetVisibles();
        },
        [
            defaultFilters,
            onChangeSelectedFilters,
            onResetVisibles,
            extraSchema,
            extraMultiSchema,
            getInitialSelected,
        ],
    );

    useEffect(() => {
        if (isFirstLoad.current) {
            getInitialSelected({
                schema: extraSchema,
                visibleFilters,
                multiSchema: extraMultiSchema,
            });
            isFirstLoad.current = false;
        }
    }, [getInitialSelected, extraSchema, visibleFilters, extraMultiSchema, defaultFilters]);

    // When is the first load, sometimes the visibleFilters are not yet loaded, so we need to control that we load them when they are.
    // That's why we use tempInnerVisibleFilters and innerVisibleFilters refs to have an internal control.
    useEffect(() => {
        if (!isFirstLoad.current && !isEqual(visibleFilters, innerVisibleFilters.current)) {
            getInitialSelected({
                schema: extraSchema,
                visibleFilters,
                multiSchema: extraMultiSchema,
            });
        }
    }, [extraMultiSchema, extraSchema, visibleFilters, getInitialSelected]);

    const modalOptionsProps = useMemo(() => {
        let baseProps = {
            title: labels.addExtraField || t('action_add_extra_field'),
            confirmText: labels?.confirm || t('action_accept'),
            cancelText: labels?.cancel || t('action_cancel'),
            deleteText: labels?.reset || t('action_reset_filter_fields_selection'),
            placeholderSearch: labels?.searchField || t('label_search_field'),
            onResetSelected,
            defaultFilters,
            forceSelectAllTypes: forceSelectAllTypes || false,
            isOpen: isOpenAddModal,
            onToggleModal: onToggleAddModal,
            onConfirm: handleConfirmAdd,
            onCancel: handleCancelAdd,
            selected: selectedFilters,
            onChangeSelected: onChangeSelectedFilters,
            handleDeleteFilter,
        };

        if (extraMultiSchema && selectorsSchema) {
            baseProps.multiSchema = extraMultiSchema;
            baseProps.selectorsSchema = selectorsSchema;
        } else {
            baseProps.schema = extraSchema;
        }

        return baseProps;
    }, [
        defaultFilters,
        extraMultiSchema,
        extraSchema,
        handleCancelAdd,
        handleConfirmAdd,
        handleDeleteFilter,
        isOpenAddModal,
        labels,
        onChangeSelectedFilters,
        onResetSelected,
        onToggleAddModal,
        selectedFilters,
        selectorsSchema,
        t,
        forceSelectAllTypes,
    ]);

    return modalOptionsProps;
};

export default useAddModal;
